// Budget API service layer
// Centralizes all Budget-related API calls with consistent error handling

import config from '../../../config';
import Notify from '../../../shared/components/widgets/Notify';

/**
 * Creates standard request headers with authentication
 * @param {string} jwtToken - JWT authentication token
 * @returns {Object} - Headers object
 */
function getAuthHeaders(jwtToken) {
  return {
    ...config.jsonHeader,
    authorization: `JWT ${jwtToken}`,
  };
}

/**
 * Standard error handler for API calls
 * @param {Error} error - The error object
 * @param {string} context - Context message for the error
 * @param {boolean} showNotification - Whether to show a notification (default: true)
 */
export function handleApiError(error, context, showNotification = true) {
  console.error(`[Budget API Error] ${context}:`, error);
  if (showNotification) {
    Notify.show('danger', 'Error', `${context}: ${error.message || error}`);
  }
  throw error;
}

/**
 * Fetches budget group data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer name
 * @param {string} params.platform - Platform key
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget group data
 */
export async function getBudgetGroups({ accountId, retailer, platform, jwtToken }) {
  const retailerPath = `${retailer.toLowerCase()}/`;
  const url = `${config.serverUrl}/accounts/${retailerPath}budget-group-daily?accountId=${accountId}&platform=${platform}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Failed to fetch budget groups');
  }
}

/**
 * Updates or creates a budget group
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget group data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetGroup({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget group has been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget group');
  }
}

/**
 * Fetches budget events
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer key
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget events data
 */
export async function getBudgetEvents({ accountId, retailer, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-event-daily?accountId=${accountId}&retailer=${retailer}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Failed to fetch budget events');
  }
}

/**
 * Updates or creates a budget event
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget event data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetEvent({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-event-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget event has been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget event');
  }
}

/**
 * Fetches budget account data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget account data
 */
export async function getBudgetAccount({ accountId, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily?accountId=${accountId}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    handleApiError(error, 'Failed to fetch budget account data', false);
    return {};
  }
}

/**
 * Fetches retailer-specific budget account data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer name (amazon, walmart, etc.)
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget account data
 */
export async function getBudgetAccountByRetailer({ accountId, retailer, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily?accountId=${accountId}&retailer=${retailer}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    handleApiError(error, 'Failed to fetch retailer-specific budget account data', false);
    return {};
  }
}

/**
 * Saves budget account settings
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget account data to save
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetAccount({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget account settings have been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget account settings');
  }
}

/**
 * Updates catalog items
 * @param {Object} params - Parameters
 * @param {Object} params.data - Catalog update data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function updateCatalogItems({ data, jwtToken }) {
  const url = `${config.serverUrl}/catalogs`;

  const requestOptions = {
    method: 'PUT',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Catalog items have been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to update catalog items');
  }
}

/**
 * Fetches platform data (spend/sales)
 * @param {Object} params - Parameters
 * @param {string} params.url - API endpoint URL
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Platform data
 */
export async function getPlatformData({ url, jwtToken }) {
  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    console.warn('Platform data not available:', error.message);
    // Return empty structure instead of throwing
    return {
      by_date: [],
      totals: { total_sales: 0, total_spend: 0 },
      by_id: { group: {} },
    };
  }
}
